import { useEffect, useState } from 'react';
import {
  getEmployeePortalUsage,
  getClientPortalUsage,
  type PortalUsageSummary,
} from '@/lib/api/portalUsage';

// Portal usage stats panel (CR #175) — shown in the Usage section of the
// employee and client detail pages. Admin previews are excluded server-side,
// so everything here is real portal traffic.

interface Props {
  kind: 'employee' | 'client';
  id: number;
}

// Plain-word labels for normalized portal API paths. Endpoints that share a
// screen share a label; unknown paths fall back to a cleaned-up path.
const AREA_LABELS: [RegExp, string][] = [
  [/^\/me\/jobs\/:id\/updates/, 'Job Updates'],
  [/^\/me\/jobs\/:id\/suggest-task/, 'Task Suggestions'],
  [/^\/me\/jobs/, 'Jobs'],
  [/^\/me\/schedule/, 'Schedule'],
  [/^\/me\/scheduled-jobs-today/, 'Schedule'],
  [/^\/me\/time-entries/, 'Timesheet'],
  [/^\/me\/pay$/, 'Pay'],
  [/^\/me\/paystubs/, 'Paystubs'],
  [/^\/me\/documents/, 'Documents'],
  [/^\/me\/flagged-docs/, 'Documents'],
  [/^\/me\/(tasks|day-tasks|checklist)/, 'Tasks & Checklists'],
  [/^\/me\/deliveries/, 'Deliveries'],
  [/^\/me\/progress/, 'Job Progress'],
  [/^\/me\/overview/, 'Overview'],
  [/^\/me\/invoices/, 'Invoices'],
  [/^\/me\/coworkers/, 'Crew'],
  [/^\/me\/shared-lists/, 'Shared Lists'],
  [/^\/me\/analyze-plan/, 'Plan Analysis'],
  [/^\/me\/notification-prefs/, 'Notification Settings'],
  [/^\/me\/permissions/, 'Portal Home'],
  [/^\/me\/profile/, 'Profile'],
  [/^\/me$/, 'Portal Home'],
];

const labelForPath = (path: string): string => {
  for (const [re, label] of AREA_LABELS) if (re.test(path)) return label;
  return path.replace(/^\/me\//, '').replace(/[/:-]+/g, ' ').trim() || 'Portal Home';
};

const fmtDateTime = (s: string | null): string => {
  if (!s) return 'Never';
  const d = new Date(s);
  if (isNaN(d.getTime())) return 'Never';
  return d.toLocaleString('en-CA', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

export const PortalUsagePanel = ({ kind, id }: Props) => {
  const [data, setData] = useState<PortalUsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const fetcher = kind === 'employee' ? getEmployeePortalUsage : getClientPortalUsage;
    fetcher(id)
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError('Could not load portal usage.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kind, id]);

  if (loading) return <div className="py-8 text-center text-sm text-gray-500">Loading portal usage…</div>;
  if (error) return <div className="py-8 text-center text-sm text-red-600">{error}</div>;
  if (!data) return null;

  if (!data.linked) {
    return (
      <div className="py-8 text-center text-sm text-gray-500">
        No portal account is linked yet — nothing to report.
        <div className="mt-1 text-xs text-gray-400">Send a portal invite from the Profile section to get them set up.</div>
      </div>
    );
  }

  // Merge normalized paths that belong to the same portal area
  const areaTotals = new Map<string, number>();
  for (const p of data.topPaths) {
    const label = labelForPath(p.path);
    areaTotals.set(label, (areaTotals.get(label) || 0) + p.count);
  }
  const areas = [...areaTotals.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  const maxArea = areas.length ? areas[0][1] : 0;

  // Last 14 days, oldest → newest, zero-filled
  const byDay = new Map(data.daily.map(d => [d.day, d.count]));
  const days: { day: string; label: string; count: number }[] = [];
  const today = new Date();
  for (let i = 13; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i, 12);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    days.push({
      day: key,
      label: d.toLocaleDateString('en-CA', { weekday: 'narrow' }),
      count: byDay.get(key) || 0,
    });
  }
  const maxDay = Math.max(1, ...days.map(d => d.count));
  const hasAnyActivity = data.logins.total > 0 || areas.length > 0 || days.some(d => d.count > 0);

  return (
    <div className="space-y-6">
      <p className="text-xs text-gray-400">
        Real portal traffic only — admin previews are never counted.
      </p>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="text-xs text-gray-500">Last login</div>
          <div className="mt-1 text-sm font-semibold text-gray-900">{fmtDateTime(data.lastLogin)}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="text-xs text-gray-500">Logins · 7 days</div>
          <div className="mt-1 text-xl font-semibold text-gray-900">{data.logins.d7}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="text-xs text-gray-500">Logins · 30 days</div>
          <div className="mt-1 text-xl font-semibold text-gray-900">{data.logins.d30}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="text-xs text-gray-500">Logins · all time</div>
          <div className="mt-1 text-xl font-semibold text-gray-900">{data.logins.total}</div>
        </div>
      </div>

      {!hasAnyActivity && (
        <div className="py-4 text-center text-sm text-gray-500">
          No portal activity recorded yet. Stats appear once they sign in and use the portal.
        </div>
      )}

      {/* Most-used areas */}
      {areas.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Most-used areas</h4>
          <div className="space-y-1.5">
            {areas.map(([label, count]) => (
              <div key={label} className="flex items-center gap-2" title={`${label}: ${count} request${count === 1 ? '' : 's'}`}>
                <div className="w-36 shrink-0 text-xs text-gray-600 truncate">{label}</div>
                <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-ochre-500 rounded-r"
                    style={{ width: `${Math.max(2, Math.round((count / maxArea) * 100))}%` }}
                  />
                </div>
                <div className="w-10 text-right text-xs text-gray-500 tabular-nums">{count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily activity — last 14 days */}
      {days.some(d => d.count > 0) && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Daily activity · last 14 days</h4>
          <div className="flex items-end gap-1 h-20">
            {days.map(d => (
              <div
                key={d.day}
                className="flex-1 flex flex-col items-center justify-end h-full"
                title={`${d.day}: ${d.count} request${d.count === 1 ? '' : 's'}`}
              >
                <div
                  className={`w-full max-w-[18px] rounded-t ${d.count ? 'bg-ochre-500' : 'bg-gray-100'}`}
                  style={{ height: d.count ? `${Math.max(8, Math.round((d.count / maxDay) * 100))}%` : '3px' }}
                />
              </div>
            ))}
          </div>
          <div className="flex gap-1 mt-1">
            {days.map(d => (
              <div key={d.day} className="flex-1 text-center text-[10px] text-gray-400">{d.label}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
